/**
 * Virtual Screening Shortcut — "screen my library", "virtual screen"
 *
 * Hit discovery path 2: dock a compound library against the loaded
 * protein using the multi-step library screening pipeline
 * (GNINA + Boltz affinity + BFD energy).
 *
 * This is a job-based workflow — it submits to the Temporal job
 * orchestrator and runs asynchronously on GPU workers.
 *
 * @module gibbs/knowledge/virtualScreeningShortcut
 */

// Keep TIGHT — canonical phrases only. The LLM handles fuzzy variants.
const VS_PHRASES = [
    'virtual screen',
    'screen my library',
    'screen the library',
    'screen compounds',
    'library screen',
    'library screening',
    'screen a library',
];

/**
 * @param {string} userMessage
 * @returns {boolean}
 */
export function matchVirtualScreeningIntent(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return false;
    const lower = userMessage.toLowerCase();
    return VS_PHRASES.some((p) => lower.includes(p));
}

// ─── Precondition check ────────────────────────────────────────────────────

/**
 * Check workspace preconditions for virtual screening.
 *
 * @param {Object} workspace - App.Workspace
 * @returns {{ready: boolean, message?: string}}
 */
export function checkVirtualScreeningPreconditions(workspace) {
    if (!workspace) {
        return { ready: false, message: 'No workspace available.' };
    }

    // Protein loaded?
    if (typeof workspace.firstCaseData === 'function') {
        const cd = workspace.firstCaseData();
        if (!cd || (typeof cd.getSolute === 'function' && !cd.getSolute())) {
            return {
                ready: false,
                message: 'I understand you want to run **virtual screening**. '
                    + 'However, I need a protein target loaded first. '
                    + 'Load a protein, then ask me again.',
            };
        }
    }

    // Compounds loaded? (need a library to screen)
    if (typeof workspace.getLoadedCompounds === 'function') {
        const compounds = workspace.getLoadedCompounds();
        if (!Array.isArray(compounds) || compounds.length === 0) {
            return {
                ready: false,
                message: 'I understand you want to run **virtual screening**. '
                    + 'However, I need compounds to screen. Import a '
                    + 'compound library first (use the sketcher, import '
                    + 'an SDF file, or load starter compounds).',
            };
        }
    }

    // Jobman available?
    if (workspace.isJobmanAvailable?.() === false) {
        return {
            ready: false,
            message: 'I understand you want to run **virtual screening**. '
                + 'However, the job manager service is not available. '
                + 'Virtual screening requires the GPU job orchestrator '
                + 'to be running. Contact your system administrator.',
        };
    }

    return { ready: true };
}

// ─── Workflow builder ──────────────────────────────────────────────────────

/**
 * Build the virtual screening workflow.
 *
 * Pipeline:
 *   1. job-library-screen — batch screen all loaded compounds
 *      against the active protein target using GNINA + Boltz affinity
 *
 * This is a single-step job submission (the Temporal workflow handles
 * the multi-step scoring internally). Results appear in the Compound
 * Table when the job completes.
 *
 * @param {string} [targetLabel='the active protein']
 * @returns {Object} workflow-intent plan
 */
export function buildVirtualScreeningWorkflow(
    targetLabel = 'the active protein',
) {
    return {
        intent: 'workflow',
        summary: `Virtual screening against ${targetLabel}: `
            + 'submit all loaded compounds to the library screening '
            + 'pipeline for batch scoring with GNINA, Boltz affinity, '
            + 'and BFD energy. Results will appear in the Compound '
            + 'Table when the job completes.',
        actions: [
            {
                actionId: 'job-library-screen',
                params: {},
                resolvedInputs: {
                    compounds: 'all',
                    targets: 'current',
                },
                step: 1,
            },
        ],
    };
}

/**
 * Top-level entry point for the virtual screening shortcut.
 *
 * @param {string} userMessage
 * @param {Object} workspaceState
 * @returns {Object|null} workflow intent, chat message, or null
 */
export function tryVirtualScreeningShortcut(userMessage, workspaceState) {
    if (!matchVirtualScreeningIntent(userMessage)) return null;

    const check = checkVirtualScreeningPreconditions(workspaceState);
    if (!check.ready) {
        return {
            intent: 'chat',
            message: check.message,
        };
    }

    return buildVirtualScreeningWorkflow();
}
